import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import useTheme from '@mui/material/styles/useTheme';
import { getIndividuals } from 'api';
import React, { FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { pushMessage } from 'redux/reducers/messages';
import { useAppDispatch } from 'redux/store';
import { Individual } from 'utils/types';
import { getMergePreview, MergePreviewData } from '../api/merge.api';
import IndividualsAutocomplete from '../components/IndividualsAutocomplete';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

function levenshtein(a: string, b: string): number {
    const matrix: number[][] = [];
    for (let i = 0; i <= b.length; i++) matrix[i] = [i];
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
            if (b.charAt(i - 1) === a.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1,
                    matrix[i][j - 1] + 1,
                    matrix[i - 1][j] + 1,
                );
            }
        }
    }
    return matrix[b.length][a.length];
}

function namesAreSimilar(name1: string, name2: string): boolean {
    const n1 = name1.toLowerCase().trim();
    const n2 = name2.toLowerCase().trim();
    if (n1 === n2) return true;
    const maxLen = Math.max(n1.length, n2.length);
    if (maxLen === 0) return true;
    const distance = levenshtein(n1, n2);
    return distance / maxLen <= 0.3;
}

interface PreviewCardProps {
    label: string;
    preview: MergePreviewData | null;
    loading: boolean;
    individual: Individual | null;
    onSelectIndividual: (individual: Individual | null) => void;
    organizationUuid: string;
    excludeUuids?: string[];
}

const PreviewCard: FC<PreviewCardProps> = ({
    label,
    preview,
    loading,
    individual,
    onSelectIndividual,
    organizationUuid,
    excludeUuids = [],
}) => {
    return (
        <Card
            sx={{
                flex: 1,
                minWidth: 340,
                maxWidth: 520,
            }}
            elevation={3}
        >
            <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {label}
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <IndividualsAutocomplete
                        organization={organizationUuid}
                        value={individual}
                        multiple={false}
                        size="small"
                        excludeUuids={excludeUuids}
                        onChange={(val: Individual | Individual[] | null) => {
                            if (Array.isArray(val)) {
                                onSelectIndividual(val[0] ?? null);
                            } else {
                                onSelectIndividual(val);
                            }
                        }}
                    />
                </Box>

                {loading && (
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Skeleton variant="circular" width={64} height={64} />
                            <Skeleton variant="text" width={160} height={32} />
                        </Box>
                        <Skeleton variant="rectangular" height={120} />
                    </Stack>
                )}

                {!loading && preview && (
                    <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                                src={preview.profile_photo ?? ''}
                                sx={{ width: 64, height: 64 }}
                            />
                            <Box>
                                <Typography variant="h6">{preview.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Created: {preview.created_at ?? 'Unknown'}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Organization
                            </Typography>
                            <Typography variant="body2">
                                {preview.organization?.name ?? 'None'}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Managing User
                            </Typography>
                            {preview.managing_user ? (
                                <Stack spacing={0.5}>
                                    <Typography variant="body2">
                                        {preview.managing_user.name}
                                        {preview.managing_user.user_name &&
                                            ` (@${preview.managing_user.user_name})`}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {preview.managing_user.email}
                                    </Typography>
                                    {preview.managing_user.phone && (
                                        <Typography variant="body2" color="text.secondary">
                                            {preview.managing_user.phone}
                                        </Typography>
                                    )}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    None
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Groups
                            </Typography>
                            {preview.groups.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {preview.groups.map((g) => (
                                        <Chip key={g.uuid} label={g.name} size="small" />
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No groups
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                GPS Sessions
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {preview.uploads.total} total
                            </Typography>
                            {Object.keys(preview.uploads.by_month).length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mb: 0.5, display: 'block' }}
                                    >
                                        Last 12 months:
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 0.5,
                                        }}
                                    >
                                        {Object.entries(preview.uploads.by_month).map(
                                            ([month, count]) => (
                                                <Chip
                                                    key={month}
                                                    label={`${month}: ${count}`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: 11 }}
                                                />
                                            ),
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                )}

                {!loading && !preview && individual && (
                    <Typography color="error" sx={{ mt: 2 }}>
                        Failed to load preview data.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

const MergeIndividuals: FC<React.PropsWithChildren<unknown>> = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const query = useQuery();
    const theme = useTheme();

    const sourceId = query.get('source');

    const [sourceIndividual, setSourceIndividual] = useState<Individual | null>(null);
    const [targetIndividual, setTargetIndividual] = useState<Individual | null>(null);

    const [sourcePreview, setSourcePreview] = useState<MergePreviewData | null>(null);
    const [targetPreview, setTargetPreview] = useState<MergePreviewData | null>(null);

    const [sourceLoading, setSourceLoading] = useState(false);
    const [targetLoading, setTargetLoading] = useState(false);

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [finalConfirmDialogOpen, setFinalConfirmDialogOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [merging, setMerging] = useState(false);

    // Load source individual from query param
    useEffect(() => {
        if (sourceId && !sourceIndividual) {
            getIndividuals({ 'filter[where_uuid]': sourceId, limit: 1 })
                .then((res) => {
                    const found = res.data.data[0];
                    if (found) {
                        setSourceIndividual(found);
                    }
                })
                .catch(() => {
                    dispatch(
                        pushMessage({
                            status: 'error',
                            message: 'Failed to load source athlete.',
                        }),
                    );
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceId]);

    // Fetch preview when source changes
    useEffect(() => {
        if (!sourceIndividual) {
            setSourcePreview(null);
            return;
        }
        setSourceLoading(true);
        getMergePreview(sourceIndividual.uuid)
            .then((res) => setSourcePreview(res.data.data))
            .catch(() => {
                dispatch(
                    pushMessage({
                        status: 'error',
                        message: 'Failed to load source athlete preview.',
                    }),
                );
            })
            .finally(() => setSourceLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceIndividual?.uuid]);

    // Fetch preview when target changes
    useEffect(() => {
        if (!targetIndividual) {
            setTargetPreview(null);
            return;
        }
        setTargetLoading(true);
        getMergePreview(targetIndividual.uuid)
            .then((res) => setTargetPreview(res.data.data))
            .catch(() => {
                dispatch(
                    pushMessage({
                        status: 'error',
                        message: 'Failed to load target athlete preview.',
                    }),
                );
            })
            .finally(() => setTargetLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetIndividual?.uuid]);

    const showNameWarning =
        sourcePreview && targetPreview && !namesAreSimilar(sourcePreview.name, targetPreview.name);

    const canMerge =
        sourceIndividual &&
        targetIndividual &&
        sourceIndividual.uuid !== targetIndividual.uuid &&
        sourcePreview &&
        targetPreview;

    const organizationUuid = sourceIndividual?.organization?.uuid ?? '';

    const handleMergeClick = () => {
        setConfirmText('');
        setConfirmDialogOpen(true);
    };

    const handleFirstConfirm = () => {
        if (confirmText !== 'I CONFIRM') return;
        setConfirmDialogOpen(false);
        setConfirmText('');
        setFinalConfirmDialogOpen(true);
    };

    const handleFinalConfirm = () => {
        setFinalConfirmDialogOpen(false);
        setMerging(true);

        // The actual merge endpoint - POST /individuals/{source}/merge-into/{target}
        // For now we'll use the pattern from the backend
        import('../../../api').then(({ Request }) => {
            Request.post(
                `/individuals/${sourceIndividual!.uuid}/merge-into/${targetIndividual!.uuid}`,
            )
                .then(() => {
                    dispatch(
                        pushMessage({
                            status: 'success',
                            message: `Successfully merged "${sourcePreview?.name}" into "${targetPreview?.name}".`,
                        }),
                    );
                    history.push(`/community/individuals/${targetIndividual!.uuid}`);
                })
                .catch((err: any) => {
                    dispatch(
                        pushMessage({
                            status: 'error',
                            message:
                                err?.response?.data?.message ??
                                'Failed to merge athletes. Please try again.',
                        }),
                    );
                })
                .finally(() => setMerging(false));
        });
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
            <Box sx={{ mb: 3 }}>
                <Button variant="text" onClick={() => history.goBack()} sx={{ mb: 1 }}>
                    &larr; Back
                </Button>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    <MergeTypeIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32 }} />
                    Merge Athletes
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    Select two athletes to merge. The source athlete (left) will be permanently
                    deleted and all their data will be transferred to the target athlete (right).
                </Typography>
            </Box>

            {showNameWarning && (
                <Alert
                    severity="warning"
                    icon={<WarningAmberIcon />}
                    sx={{
                        mb: 3,
                        fontSize: 16,
                        '& .MuiAlert-message': { fontWeight: 500 },
                    }}
                >
                    Warning: The names of these athletes do not match! &quot;
                    {sourcePreview?.name}&quot; and &quot;{targetPreview?.name}&quot; appear to be
                    different athletes. Please double-check before merging.
                </Alert>
            )}

            {sourceIndividual &&
                targetIndividual &&
                sourceIndividual.uuid === targetIndividual.uuid && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        You cannot merge an athlete into themselves. Please select two different
                        athletes.
                    </Alert>
                )}

            <Box
                sx={{
                    display: 'flex',
                    gap: 3,
                    alignItems: 'flex-start',
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                }}
            >
                {/* Source (will be deleted) */}
                <Box sx={{ flex: 1, minWidth: 340 }}>
                    <Paper
                        sx={{
                            p: 1,
                            mb: 1,
                            backgroundColor: `${theme.palette.error.light}22`,
                            border: `2px solid ${theme.palette.error.main}`,
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, color: theme.palette.error.main }}
                        >
                            SOURCE — Will Be Deleted
                        </Typography>
                    </Paper>
                    <PreviewCard
                        label="Source Athlete"
                        preview={sourcePreview}
                        loading={sourceLoading}
                        individual={sourceIndividual}
                        onSelectIndividual={setSourceIndividual}
                        organizationUuid={organizationUuid}
                    />
                </Box>

                {/* Arrow */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 200,
                        pt: 8,
                    }}
                >
                    <ArrowForwardIcon
                        sx={{
                            fontSize: 64,
                            color: theme.palette.primary.main,
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            mt: 1,
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            textAlign: 'center',
                        }}
                    >
                        Merging Into
                    </Typography>
                </Box>

                {/* Target (will keep) */}
                <Box sx={{ flex: 1, minWidth: 340 }}>
                    <Paper
                        sx={{
                            p: 1,
                            mb: 1,
                            backgroundColor: `${theme.palette.success.light}22`,
                            border: `2px solid ${theme.palette.success.main}`,
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, color: theme.palette.success.main }}
                        >
                            TARGET — Will Be Kept
                        </Typography>
                    </Paper>
                    <PreviewCard
                        label="Target Athlete"
                        preview={targetPreview}
                        loading={targetLoading}
                        individual={targetIndividual}
                        onSelectIndividual={setTargetIndividual}
                        organizationUuid={organizationUuid}
                        excludeUuids={sourceIndividual ? [sourceIndividual.uuid] : []}
                    />
                </Box>
            </Box>

            {/* Merge Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button
                    variant="contained"
                    color="error"
                    size="large"
                    disabled={!canMerge || merging}
                    onClick={handleMergeClick}
                    startIcon={
                        merging ? <CircularProgress size={20} color="inherit" /> : <MergeTypeIcon />
                    }
                    sx={{ px: 6, py: 1.5, fontSize: 16, fontWeight: 700 }}
                >
                    {merging ? 'Merging...' : 'Merge Athletes'}
                </Button>
            </Box>

            {/* First Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: theme.palette.error.main, fontWeight: 700 }}>
                    <WarningAmberIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Confirm Athlete Merge
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        You are about to merge <strong>&quot;{sourcePreview?.name}&quot;</strong>{' '}
                        into <strong>&quot;{targetPreview?.name}&quot;</strong>.
                    </DialogContentText>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <strong>This action is permanent and cannot be undone.</strong>
                        <br />
                        The source athlete &quot;{sourcePreview?.name}&quot; will be{' '}
                        <strong>permanently deleted</strong>. All performance data, group
                        memberships, external IDs, and other records will be transferred to the
                        target athlete.
                    </Alert>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        To confirm, type <strong>I CONFIRM</strong> in the box below:
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        variant="outlined"
                        placeholder="Type I CONFIRM"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        error={confirmText.length > 0 && confirmText !== 'I CONFIRM'}
                        helperText={
                            confirmText.length > 0 && confirmText !== 'I CONFIRM'
                                ? 'Please type exactly: I CONFIRM'
                                : ''
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={confirmText !== 'I CONFIRM'}
                        onClick={handleFirstConfirm}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Final Confirmation Dialog */}
            <Dialog
                open={finalConfirmDialogOpen}
                onClose={() => setFinalConfirmDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: theme.palette.error.main, fontWeight: 700 }}>
                    Final Confirmation
                </DialogTitle>
                <DialogContent>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <strong>Last chance to cancel!</strong>
                    </Alert>
                    <DialogContentText>
                        Are you absolutely sure you want to permanently delete{' '}
                        <strong>&quot;{sourcePreview?.name}&quot;</strong> and transfer all their
                        data to <strong>&quot;{targetPreview?.name}&quot;</strong>?
                        <br />
                        <br />
                        This cannot be reversed.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFinalConfirmDialogOpen(false)} variant="outlined">
                        Cancel — Go Back
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleFinalConfirm}
                        sx={{ fontWeight: 700 }}
                    >
                        Yes, Permanently Merge
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MergeIndividuals;
